import { TranslationService } from './services/translationService';

import { AbstractControl } from '@angular/forms';
import { ConsumerJsonModel } from '../consumer/consumer.json.model';
import { SubjectJsonModel } from '../subject/subject.json.model';

const mongoIdRegex = /^[0-9a-fA-F]{24}$/;
export const MIN_NAME_LENGTH = 8; // Min length for names
export type ReadonlyAction = 'set' | 'remove';

/**
 * returns true if the given string is a valid MongoDB ObjectId
 * @param id the string to check
 * @returns true if the given string is a valid MongoDB ObjectId
 */
export function isMongoId(id: string | number | undefined): boolean {
  return typeof id === 'string' && mongoIdRegex.test(id);
}

/**
 * generates a secure password with a length of 16 characters
 * @returns a secure password
 */
export function generateSecurePassword(): string {
  const length = 16;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const specialChars = '!?&w';
  const allChars = charset + specialChars;

  let password = '';

  password += charset[Math.floor(Math.random() * charset.length)];
  password += charset[Math.floor(Math.random() * charset.length)];
  password += '0123456789'[Math.floor(Math.random() * 10)];
  password += specialChars[Math.floor(Math.random() * specialChars.length)];

  // Fülle den Rest des Passworts mit zufälligen Zeichen
  for (let i = 4; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * allChars.length);
    password += allChars[randomIndex];
  }

  return shuffleString(password);
}

/**
 * shuffles the characters of a string
 * @param str the string to shuffle
 * @returns the shuffled string
 */
function shuffleString(str: string): string {
  const array = str.split('');
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array.join('');
}

export function getKind(translationService: TranslationService, kind: string): string {
  let translatedText = translationService.translate(kind) || `?${kind}?`;
  if (translatedText) {
    translatedText = translatedText.replace(/ae/g, 'ä;').replace(/oe/g, 'ö').replace(/ue/g, 'ü');
    translatedText = translatedText.charAt(0).toUpperCase() + translatedText.slice(1);

    return translatedText;
  }
  return kind;
}

export function urlValidator(control: AbstractControl): Record<string, unknown> | null {
  return URL.canParse(control.value) ? null : { invalidUrl: true };
}

export function checkForNameErrors(formControl: AbstractControl): string | null {
  if (formControl.value.length === 0) {
    return 'Eingabe erforderlich';
  }

  if (formControl.value.length < MIN_NAME_LENGTH) {
    return `Eingabe muss mindestens ${MIN_NAME_LENGTH} Zeichen haben`;
  }

  return null;
}

export function doesConsumerExist(consumers: ConsumerJsonModel[], activeConsumer: ConsumerJsonModel): boolean {
  if (!activeConsumer?.name) {
    return false;
  }
  const foundConsumer = consumers.find((entry) => {
    return entry.id === activeConsumer.id;
  });
  return (foundConsumer ? true : false);
}

export function getConsumerErrorText(): string {
  return 'Konsument existiert nicht mehr';
}

export function doesSubjectExist(subjects: SubjectJsonModel[], id: string): boolean {
  const foundSubject = subjects.find((entry) => {
    return entry.treeId === id;
  });
  return (foundSubject ? true : false);
}

// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export function getMinFilterLengthText(minLen: number = 3): string {
  return `Mindestens ${minLen} Zeichen lang`;
}

export function getSubjectErrorText(): string {
  return 'Fach/Beruf existiert nicht mehr';
}

export function toggleReadOnlyMathEditorInput(htmlString: string, action: ReadonlyAction): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const matheditorInputs = doc.querySelectorAll('matheditor-input');

  matheditorInputs.forEach(input => {
    const value = input.getAttribute('value');

    // Bedingung: value existiert und ist nicht leer (nach Trimmen von Leerzeichen)
    const hasValue = (value !== null && value.trim() !== '');

    if (action === 'set' && hasValue) {
      input.setAttribute('readonly', '');
    }
    else if (action === 'remove') {
      input.removeAttribute('readonly');
    }
  });

  return doc.body.innerHTML;
}

export function toggleContenteditable(htmlString: string, action: ReadonlyAction): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Selektiere alle Elemente mit der Klasse 'editable-box-header' oder 'editable-box-content'
  const editableElements = doc.querySelectorAll('.editable-box-header, .editable-box-content');

  editableElements.forEach(element => {
    if (action === 'set') {
      element.setAttribute('contenteditable', 'true');
    }
    else if (action === 'remove') {
      element.removeAttribute('contenteditable');
    }
  });

  return doc.body.innerHTML;
}
